import { api, useAuthStore, useIsPlatformHost } from '@bemor/web-kit';
import {
    Button,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RowActions,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { FraudReasonCode } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';

const PATH = '/staff/risk/fraud-codes';
const NETWORKS = ['visa', 'mastercard', 'amex', 'discover', 'other'] as const;

function CodeSheet({ onClose }: { onClose: () => void }) {
    const [code, setCode] = useState('');
    const [network, setNetwork] = useState<string>('visa');
    const [label, setLabel] = useState('');
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(
        async () =>
            (
                await api().put<FraudReasonCode>(
                    `${PATH}/${encodeURIComponent(code.trim())}`,
                    { network, label },
                )
            ).data,
        [PATH],
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="Add a fraud reason code"
            submitLabel="Save code"
            busy={write.isPending}
            submitDisabled={code.trim() === '' || label.trim() === ''}
            dirty
            error={error}
            data-testid="fraud-code-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <Field
                id="fc-code"
                label="Code"
                hint="As the network writes it (10.4, 4837)"
            >
                <Input
                    id="fc-code"
                    maxLength={16}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                />
            </Field>
            <Field id="fc-network" label="Network">
                <Select value={network} onValueChange={setNetwork}>
                    <SelectTrigger id="fc-network">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {NETWORKS.map((n) => (
                            <SelectItem key={n} value={n}>
                                {n}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field id="fc-label" label="Label">
                <Input
                    id="fc-label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                />
            </Field>
        </FormSheet>
    );
}

/** BM-125 decision 2: the networks' fraud reason codes; a dispute opened with one is fraud unless staff say otherwise. */
export function FraudCodesPage() {
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const codes = useOne<FraudReasonCode[]>(PATH);
    const [adding, setAdding] = useState(false);
    const remove = useWrite(
        async (code: string) =>
            (await api().delete(`${PATH}/${encodeURIComponent(code)}`)).data,
        [PATH],
    );
    const editable = platformHost && can(P.riskRulesManage);
    return (
        <div>
            <PageHeader
                title="Fraud reason codes"
                meta="A dispute opened with one of these codes counts as fraud (the fraud ratio, the fraud_rate alert); edited on the platform host."
                primaryAction={
                    editable ? (
                        <Button onClick={() => setAdding(true)}>
                            Add code
                        </Button>
                    ) : undefined
                }
            />
            <div className="overflow-x-auto">
                <Table data-testid="fraud-codes">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Network</TableHead>
                            <TableHead>Label</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(codes.data?.data ?? []).map((c) => (
                            <TableRow key={c.code}>
                                <TableCell className="font-mono text-xs">
                                    {c.code}
                                </TableCell>
                                <TableCell>{c.network}</TableCell>
                                <TableCell>{c.label}</TableCell>
                                <TableCell>
                                    {editable ? (
                                        <RowActions
                                            name={c.code}
                                            items={[
                                                {
                                                    label: 'Remove',
                                                    destructive: true,
                                                    onSelect: () =>
                                                        remove.mutate(c.code),
                                                },
                                            ]}
                                        />
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {adding ? <CodeSheet onClose={() => setAdding(false)} /> : null}
        </div>
    );
}
